import * as React from 'react'
import { useParams } from 'react-router-dom'

import {
  SurveyShowPageEmployeesDocument,
  SurveyShowPagePoliciesDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import i18n from 'modules/core/lib/i18n'
import NotFound from 'modules/core/components/NotFound'
import asyncAction from 'modules/core/lib/asyncAction'
import { apiClient } from 'modules/core/lib/mobx-rest'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'

import { useFeatures } from 'modules/core/hooks/useFeatures'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useWorkflowSurveysContext } from 'modules/surveys/hooks/useWorkflowSurveysContext'
import SurveyShowWrapper from 'modules/surveys/components/Show/SurveyShowWrapper'
import { useQuestionGroups } from 'modules/surveys/hooks/useQuestionGroups'
import { useSurvey } from 'modules/surveys/hooks/useSurvey'
import { useSurveyFormV1 } from 'modules/surveys/hooks/useSurveyFormV1'
import { canEditSurvey } from 'modules/surveys/lib/canEditSurvey'

import type { EditorInfo } from './hooks'
import { SurveyShowContext } from './hooks'

const SurveyShowPage = () => {
  const { id } = useParams()
  const { loading, form } = useSurveyFormV1(String(id))
  const { loading: loadingSurvey, survey } = useSurvey(id)
  const { loading: loadingQuestionGroups, questionGroups } = useQuestionGroups(String(id))

  const { data: policiesData } = useQuery(SurveyShowPagePoliciesDocument, {
    variables: { surveyId: id ?? '' },
    skip: !id,
  })

  const authorId = survey?.attributes.author_id
  const supervisorIds = survey?.attributes.supervisor_ids ?? []
  const accessIds = Array.from(new Set([authorId, ...supervisorIds].filter(Boolean).map(String)))

  const { data: employeesData, loading: loadingEmployees } = useQuery(
    SurveyShowPageEmployeesDocument,
    {
      variables: { accessIds },
      skip: accessIds.length === 0,
    }
  )

  const { canManageSurveys } = useWorkflowSurveysContext()
  const { has_surveys_using_forms: smartSurveysEnabled, loadingFeatures } = useFeatures([
    'surveys_using_forms',
  ])
  const [canbeStarted, setCanBeStarted] = React.useState(false)

  const fetchCanBeStarted = async () => {
    const api = apiClient()
    const result = await api.get(`/surveys/v2/surveys/${id}/status`)
    setCanBeStarted(result.can_be_started)
  }

  const updateCanBeStarted = async () =>
    await asyncAction(fetchCanBeStarted, {
      errorFlash: false,
      successFlash: false,
    })

  if (loading || loadingSurvey || loadingQuestionGroups) return <Loading />

  if (!loadingSurvey && !survey) return <NotFound />

  // Wait for the employees query before deriving names so we don't briefly
  // render every author/editor as "former employee" while it's in flight.
  if (accessIds.length > 0 && loadingEmployees) return <Loading />

  const canEdit = survey ? canEditSurvey(survey, canManageSurveys) : false
  const canEditForm = policiesData?.surveys?.survey?._policies?.edit?.can ?? false
  const isClassicCustomSurvey =
    !survey?.attributes.source || survey.attributes.source === 'custom_survey'

  const employeeNodes = employeesData?.employees.employeeNamesConnection.nodes ?? []
  const employeeByAccessId = new Map(
    employeeNodes.map((node) => [String(node.access.id), node] as const)
  )

  const authorEmployee = authorId ? employeeByAccessId.get(String(authorId)) : undefined
  const authorName = authorEmployee
    ? getFullDisplayName({
        firstName: authorEmployee.firstName,
        lastName: authorEmployee.lastName,
      })
    : i18n.t('surveys.show.former_employee')

  const editors: EditorInfo[] = supervisorIds.map((supervisorId) => {
    const employee = employeeByAccessId.get(String(supervisorId))
    if (!employee) {
      // Access could not be resolved (e.g., deleted) — keep a row so the
      // supervisor remains visible and removable.
      return {
        accessId: String(supervisorId),
        firstName: i18n.t('surveys.show.former_employee'),
        lastName: '',
        avatar: null,
      }
    }
    return {
      accessId: String(employee.access.id),
      firstName: employee.firstName,
      lastName: employee.lastName,
      avatar: employee.access.avatar ?? null,
    }
  })

  const surveyShowContextValues = {
    form: form,
    survey: survey,
    hasSurveyV2: true,
    isAnonymous: !!survey && survey.isAnonymous,
    canbeStarted: canbeStarted,
    updateCanBeStarted: updateCanBeStarted,
    canEditSurvey: canEdit,
    canEditSurveyForm: canEditForm,
    loading: loadingSurvey,
    isClassicSurveyDisabled: (loadingFeatures || smartSurveysEnabled) && isClassicCustomSurvey,
    authorName,
    editors,
  }

  return (
    <SurveyShowContext.Provider value={surveyShowContextValues}>
      <SurveyShowWrapper surveyId={String(id)} questionGroups={questionGroups} />
    </SurveyShowContext.Provider>
  )
}

export default SurveyShowPage
